import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { authenticatedFetch } from './authService';
import AppEmptyState from '../UI/AppEmptyState';

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  notificationType: string;
  createdAt: string;
  readAt?: string | null;
}

const formatCreatedAt = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    setLoading(true);
    setError(false);
    try {
      const [itemsResponse, countResponse] = await Promise.all([
        authenticatedFetch('/notifications?limit=10'),
        authenticatedFetch('/notifications/unread-count'),
      ]);
      if (!itemsResponse.ok || !countResponse.ok) throw new Error('Unable to load notifications');
      const nextItems = (await itemsResponse.json()) as NotificationItem[];
      const countPayload = (await countResponse.json()) as { count: number };
      setItems(nextItems);
      setUnreadCount(countPayload.count || 0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = window.setInterval(loadNotifications, 30000);
    window.addEventListener('focus', loadNotifications);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', loadNotifications);
    };
  }, [loadNotifications]);

  const markRead = async (id: number) => {
    try {
      const response = await authenticatedFetch(`/notifications/${id}/read`, { method: 'PATCH' });
      if (!response.ok) throw new Error('Unable to mark notification read');
      setItems((current) => current.map((item) => item.id === id ? { ...item, readAt: new Date().toISOString() } : item));
      setUnreadCount((current) => Math.max(0, current - 1));
    } catch {
      setError(true);
    }
  };

  const markAllRead = async () => {
    try {
      const response = await authenticatedFetch('/notifications/read-all', { method: 'PATCH' });
      if (!response.ok) throw new Error('Unable to mark notifications read');
      setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      setError(true);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          aria-haspopup="true"
          aria-expanded={anchorEl ? 'true' : undefined}
          onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
          sx={{ borderRadius: 2, '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
        >
          <Badge badgeContent={unreadCount > 99 ? '99+' : unreadCount} color="secondary" max={99}>
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 24px)', sm: 380 }, maxWidth: 380, mt: 1 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
            {unreadCount > 0 ? (
              <Box
                component="button"
                type="button"
                onClick={markAllRead}
                sx={{ border: 0, background: 'transparent', color: 'primary.main', cursor: 'pointer', font: 'inherit', fontSize: '0.8rem', fontWeight: 700, p: 0.5 }}
              >
                Mark all read
              </Box>
            ) : null}
          </Stack>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : error ? (
          <AppEmptyState message="Notifications are temporarily unavailable." minHeight={120} />
        ) : items.length === 0 ? (
          <AppEmptyState message="You are all caught up." minHeight={120} />
        ) : (
          <List disablePadding sx={{ maxHeight: { xs: '60vh', sm: 420 }, overflowY: 'auto' }}>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => item.readAt ? undefined : markRead(item.id)}
                sx={{ alignItems: 'flex-start', gap: 1, bgcolor: item.readAt ? 'transparent' : 'action.hover' }}
              >
                <Box sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: '50%', bgcolor: item.readAt ? 'transparent' : 'secondary.main', mt: 1 }} aria-hidden="true" />
                <ListItemText
                  primary={item.title}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" display="block" sx={{ overflowWrap: 'anywhere' }}>{item.message}</Typography>
                      <Typography component="span" variant="caption" display="block">{formatCreatedAt(item.createdAt)}</Typography>
                    </>
                  }
                  primaryTypographyProps={{ fontWeight: item.readAt ? 500 : 700, sx: { overflowWrap: 'anywhere' } }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
